// DOM effects layer: flying tokens (3D → HUD handoff), floating numbers, banners, stamp showcase.
// Everything returns promises so beats can sequence it. docs/06 §5.
import { gsap } from 'gsap';

export interface Point {
  x: number;
  y: number;
}

let layer: HTMLDivElement | null = null;
let showcaseHandler: ((s: ShowcaseRequest) => Promise<void>) | null = null;

export function bindFxLayer(el: HTMLDivElement | null): void {
  layer = el;
}

export interface ShowcaseRequest {
  kind: 'spell' | 'action' | 'problem' | 'decree' | 'resolution';
  def: string;
  stamp: string;
  color: string;
  speed: number;
  small?: boolean;
  /** Where the card comes from (the player who played it); it grows out of this point. */
  from?: Point | null;
  /** Where the card goes afterwards (a tableau, the discard pile); it shrinks into this point. */
  to?: Point | null;
  /** The least time, in real seconds whatever the speed, the card rests stamped at the centre. */
  hold?: number;
}

export function bindShowcase(fn: ((s: ShowcaseRequest) => Promise<void>) | null): void {
  showcaseHandler = fn;
}

export function showcase(req: ShowcaseRequest): Promise<void> {
  return showcaseHandler ? showcaseHandler(req) : Promise.resolve();
}

/** Screen-space centre of a HUD anchor (`data-anchor="..."`), trying names in order. */
export function anchor(...names: string[]): Point | null {
  for (const n of names) {
    const el = document.querySelector<HTMLElement>(`[data-anchor="${n}"]`);
    if (el && el.offsetParent !== null) {
      const r = el.getBoundingClientRect();
      if (r.width || r.height) return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    }
  }
  return null;
}

export function bump(...names: string[]): void {
  for (const n of names) {
    const el = document.querySelector<HTMLElement>(`[data-anchor="${n}"]`);
    if (el) {
      gsap.fromTo(el, { scale: 1.35 }, { scale: 1, duration: 0.35, ease: 'back.out(3)', clearProps: 'scale' });
      return;
    }
  }
}

export interface FlyOptions {
  from: Point;
  to: Point;
  html: string;
  className?: string;
  duration?: number;
  delay?: number;
  speed?: number;
  arc?: number;
}

/** A token flies along a quadratic arc from `from` to `to` (source → destination causality). */
export function fly(o: FlyOptions): Promise<void> {
  if (!layer) return Promise.resolve();
  const el = document.createElement('div');
  el.className = `fx-token ${o.className ?? ''}`;
  el.innerHTML = o.html;
  layer.appendChild(el);
  const speed = o.speed ?? 1;
  const dur = (o.duration ?? 0.55) / speed;
  const arc = o.arc ?? -Math.min(160, Math.hypot(o.to.x - o.from.x, o.to.y - o.from.y) * 0.35);
  const ctrl = { x: (o.from.x + o.to.x) / 2, y: Math.min(o.from.y, o.to.y) + arc };
  const p = { t: 0 };
  gsap.set(el, { x: o.from.x, y: o.from.y, xPercent: -50, yPercent: -50, scale: 0.4, opacity: 0 });
  return new Promise((resolve) => {
    gsap
      .timeline({ delay: (o.delay ?? 0) / speed, onComplete: () => (el.remove(), resolve()) })
      .to(el, { scale: 1, opacity: 1, duration: 0.12 / speed, ease: 'back.out(2)' })
      .to(
        p,
        {
          t: 1,
          duration: dur,
          ease: 'power2.inOut',
          onUpdate: () => {
            const t = p.t;
            const x = (1 - t) * (1 - t) * o.from.x + 2 * (1 - t) * t * ctrl.x + t * t * o.to.x;
            const y = (1 - t) * (1 - t) * o.from.y + 2 * (1 - t) * t * ctrl.y + t * t * o.to.y;
            gsap.set(el, { x, y });
          },
        },
        '<',
      )
      .to(el, { scale: 0.6, opacity: 0, duration: 0.1 / speed });
  });
}

/** A floating "+2" that rises and fades. */
export function float(at: Point, text: string, className = '', speed = 1): void {
  if (!layer) return;
  const el = document.createElement('div');
  el.className = `fx-float ${className}`;
  el.innerHTML = text;
  layer.appendChild(el);
  gsap.set(el, { x: at.x, y: at.y, xPercent: -50, yPercent: -50 });
  gsap
    .timeline({ onComplete: () => el.remove() })
    .timeScale(speed)
    .fromTo(el, { opacity: 0, scale: 0.6 }, { opacity: 1, scale: 1, duration: 0.15, ease: 'back.out(2)' })
    .to(el, { y: at.y - 46, duration: 0.9, ease: 'power1.out' }, 0)
    .to(el, { opacity: 0, duration: 0.3 }, 0.6);
}

/**
 * The on-screen box of the first element matching any of the selectors, at its layout size: a
 * momentary scale (an arrival bump) is taken out, while its position (including any offset) stays.
 */
export function rectOf(...selectors: string[]): DOMRect | null {
  for (const sel of selectors) {
    const el = document.querySelector<HTMLElement>(sel);
    if (el && el.offsetParent !== null) {
      const r = el.getBoundingClientRect();
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      return new DOMRect(r.left + r.width / 2 - w / 2, r.top + r.height / 2 - h / 2, w, h);
    }
  }
  return null;
}

/**
 * A small plate that slides out from under a panel (below it, or from its right edge), holds long
 * enough to read, and tucks back in. A clip keeps it looking as if it emerges from behind the panel.
 */
export function plate(rect: DOMRect | null, who: string, what: string, speed = 1, side: 'below' | 'right' = 'below', accent?: string, hold = 1.5): Promise<void> {
  if (!layer || !rect) return Promise.resolve();
  const el = document.createElement('div');
  el.className = `fx-plate fx-plate-${side}`;
  // Two lines with their own weights: who (a quiet name), then what happened (the event, bold).
  const name = document.createElement('div');
  name.className = 'fx-plate-who';
  name.textContent = who;
  const event = document.createElement('div');
  event.className = 'fx-plate-what';
  event.textContent = what;
  el.append(name, event);
  if (accent) el.style.setProperty('--accent', accent);
  layer.appendChild(el);
  const below = side === 'below';
  // From the right edge it comes out below the rivals bar, so it never covers a rival's card.
  const barBottom = rectOf('.rivals-bar')?.bottom ?? 0;
  // Below a panel it is exactly the panel's width, as if it were part of it.
  gsap.set(el, below ? { left: rect.left, top: rect.bottom - 2, width: rect.width } : { left: rect.right - 2, top: Math.max(rect.top + 22, barBottom + 18) });
  const hidden = below ? { yPercent: -100, clipPath: 'inset(100% 0% 0% 0%)' } : { xPercent: -100, clipPath: 'inset(0% 0% 0% 100%)' };
  const shown = below ? { yPercent: 0, clipPath: 'inset(0% 0% 0% 0%)' } : { xPercent: 0, clipPath: 'inset(0% 0% 0% 0%)' };
  return new Promise((resolve) => {
    gsap
      .timeline({ onComplete: () => (el.remove(), resolve()) })
      .timeScale(speed)
      .fromTo(el, hidden, { ...shown, duration: 0.34, ease: 'power3.out' })
      .to(el, { ...hidden, duration: 0.28, ease: 'power2.in' }, `+=${hold}`);
  });
}

/** A card stack's timing at 1×: the gap between one card dropping out and the next, and each drop. */
export const STACK_STAGGER = 0.14;
export const STACK_DROP = 0.4;

export interface CardStackOptions {
  /** Below a panel (a rival's card), or out of its right edge (your own panel). */
  side: 'below' | 'right';
  /** Scales the motion only: the rest is set by `until`. */
  speed: number;
  accent?: string;
  /** Where this stack's first card falls in the moment's stagger, when several stacks drop together. */
  first?: number;
  /** Real seconds from now at which the stack tucks back in. */
  until: number;
}

/**
 * A titled stack of cards (HTML, one element each) that drop out from under a panel one after
 * another, like `plate`, rest until `until`, then tuck back in together. Resolves once they are away.
 */
export function cardStack(rect: DOMRect | null, title: string, cards: string[], o: CardStackOptions): Promise<void> {
  if (!layer || !rect || !cards.length) return Promise.resolve();
  const below = o.side === 'below';
  // The outer box clips at the panel's edge, so the stack seems to come from behind it.
  const clip = document.createElement('div');
  clip.className = `fx-stack fx-stack-${o.side}`;
  if (o.accent) clip.style.setProperty('--accent', o.accent);
  const inner = document.createElement('div');
  inner.className = 'fx-stack-inner';
  const head = document.createElement('div');
  head.className = 'fx-stack-head';
  head.textContent = title;
  inner.append(head);
  for (const html of cards) inner.insertAdjacentHTML('beforeend', html);
  clip.append(inner);
  layer.appendChild(clip);
  if (below) {
    // The panel's own width, as if part of it (a window too narrow for that gets a readable width).
    const width = Math.max(rect.width, 180);
    gsap.set(clip, { left: Math.min(rect.left, innerWidth - width - 8), top: rect.bottom - 2, width });
  } else {
    // From the right edge it comes out below the rivals bar, so it never covers a rival's card,
    // nor any stack already hanging from one (start those first).
    const left = rect.right - 2;
    const width = Math.min(300, innerWidth - rect.right - 12);
    let top = Math.max(rect.top + 22, (rectOf('.rivals-bar')?.bottom ?? 0) + 18);
    for (const other of layer.querySelectorAll<HTMLElement>('.fx-stack-below')) {
      const r = other.getBoundingClientRect();
      if (r.left < left + width && r.right > left) top = Math.max(top, r.bottom + 12);
    }
    gsap.set(clip, { left, top, width });
    // …but on screen, even if that means covering one on a short window.
    gsap.set(clip, { top: Math.max(8, Math.min(top, innerHeight - clip.offsetHeight - 8)) });
  }
  // The title comes out with the first card; each card travels from behind the panel's edge to its
  // place in the same time, so a later card always emerges from under the one before it.
  const parts = [...inner.children] as HTMLElement[];
  const first = o.first ?? 0;
  const tl = gsap.timeline();
  parts.forEach((el, i) => {
    const from = below ? { y: -(el.offsetTop + el.offsetHeight) } : { x: -(el.offsetLeft + el.offsetWidth) };
    const at = ((first + Math.max(0, i - 1)) * STACK_STAGGER) / o.speed;
    tl.fromTo(el, from, { x: 0, y: 0, duration: STACK_DROP / o.speed, ease: 'power3.out' }, at);
  });
  tl.to(inner, { ...(below ? { yPercent: -100 } : { xPercent: -100 }), duration: 0.3 / o.speed, ease: 'power2.in' }, Math.max(o.until, tl.duration()));
  return new Promise((resolve) => tl.eventCallback('onComplete', () => (clip.remove(), resolve())));
}

/** A title banner across the board ("Your turn", "Round 3 · 11:00 AM"). */
export function banner(title: string, subtitle = '', speed = 1, kind = '', accent?: string): Promise<void> {
  if (!layer) return Promise.resolve();
  const el = document.createElement('div');
  el.className = `fx-banner ${kind}`;
  if (accent) el.style.setProperty('--accent', accent);
  el.innerHTML = `<div class="fx-banner-title">${title}</div>${subtitle ? `<div class="fx-banner-sub">${subtitle}</div>` : ''}`;
  layer.appendChild(el);
  return new Promise((resolve) => {
    gsap
      .timeline({ onComplete: () => (el.remove(), resolve()) })
      .timeScale(speed)
      .fromTo(el, { opacity: 0, scaleX: 0.6 }, { opacity: 1, scaleX: 1, duration: 0.22, ease: 'power3.out' })
      .to(el, { opacity: 0, y: -12, duration: 0.25, ease: 'power2.in' }, '+=0.75');
  });
}

/**
 * The Fog Lifts (P-17): a bank of mist over an area (the rivals' cards) that thins and drifts
 * away, leaving what was hidden in plain view.
 */
export function fogClear(rect: DOMRect | null, speed = 1): Promise<void> {
  if (!layer || !rect) return Promise.resolve();
  const el = document.createElement('div');
  el.className = 'fx-fog';
  Object.assign(el.style, { left: `${rect.left - 30}px`, top: `${rect.top - 16}px`, width: `${rect.width + 60}px`, height: `${rect.height + 32}px` });
  for (let i = 0; i < 5; i++) {
    const puff = document.createElement('span');
    puff.className = 'fx-fog-puff';
    puff.style.left = `${8 + i * 19}%`;
    puff.style.animationDelay = `${i * 0.12}s`;
    el.appendChild(puff);
  }
  layer.appendChild(el);
  return new Promise((resolve) => {
    gsap
      .timeline({ onComplete: () => (el.remove(), resolve()) })
      .timeScale(speed)
      .fromTo(el, { opacity: 0 }, { opacity: 1, duration: 0.25 })
      .to(el, { opacity: 0, x: 60, filter: 'blur(10px)', duration: 0.9, ease: 'power1.in' }, '+=0.35');
  });
}
